import React, { FC, useMemo, useState } from 'react';
import { formatStatusLabel, sftpStatusBadgeClass } from './sftp-transfer-status.utils';

export interface ISftpStatusHistoryEntry {
    id?: number;
    fromStatus?: string;
    toStatus?: string;
    message?: string;
    detailsJson?: string;
    createdAt?: string;
}

const FAILURE_STATUSES = new Set([
    'VALIDATION_FAILED',
    'FORMAT_UNKNOWN',
    'FAILED',
    'BATCH_SETUP_FAILED',
    'DSCSA_FAILED',
    'PARSE_FAILED',
    'ADMIN_REJECTED',
    'DLQ',
]);

function formatTimestamp(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}

function stepIcon(toStatus?: string, isLast: boolean): 'done' | 'failed' | 'current' | 'pending' {
    if (!toStatus) return 'pending';
    if (FAILURE_STATUSES.has(toStatus)) return 'failed';
    if (isLast) return 'current';
    return 'done';
}

const SftpTransferTimeline: FC<{
    history: ISftpStatusHistoryEntry[];
    currentStatus?: string;
}> = ({ history, currentStatus }) => {
    const [expanded, setExpanded] = useState(false);

    const sorted = useMemo(
        () =>
            [...history].sort((a, b) => {
                const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return ta - tb;
            }),
        [history]
    );

    const visible = expanded ? sorted : sorted.slice(-5);
    const hiddenCount = sorted.length - visible.length;

    if (sorted.length === 0) {
        return (
            <p className="text-sm text-slate-500">
                No status history yet. Current status:{' '}
                <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${sftpStatusBadgeClass(currentStatus)}`}>
                    {formatStatusLabel(currentStatus)}
                </span>
            </p>
        );
    }

    return (
        <div>
            <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Lifecycle</h4>
                {sorted.length > 5 && (
                    <button
                        type="button"
                        className="text-xs font-semibold text-[#bd902e] hover:underline"
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded ? 'Show recent only' : `View all (${sorted.length})`}
                    </button>
                )}
            </div>
            {!expanded && hiddenCount > 0 && (
                <p className="mb-2 text-xs text-slate-500">{hiddenCount} earlier step(s) hidden</p>
            )}
            <ol className="relative border-l border-slate-200 pl-4">
                {visible.map((entry, index) => {
                    const isLast = entry.toStatus === currentStatus && index === visible.length - 1;
                    const icon = stepIcon(entry.toStatus, isLast);
                    return (
                        <li key={entry.id ?? `${entry.toStatus}-${entry.createdAt}-${index}`} className="mb-4 ml-2 last:mb-0">
                            <span
                                className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold ${
                                    icon === 'failed'
                                        ? 'bg-red-500 text-white'
                                        : icon === 'current'
                                          ? 'bg-[#bd902e] text-white'
                                          : icon === 'done'
                                            ? 'bg-green-500 text-white'
                                            : 'bg-slate-300 text-slate-600'
                                }`}
                            >
                                {icon === 'failed' ? '!' : icon === 'done' ? '✓' : icon === 'current' ? '●' : ''}
                            </span>
                            <div className="flex flex-wrap items-center gap-2">
                                <span
                                    className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${sftpStatusBadgeClass(entry.toStatus)}`}
                                >
                                    {formatStatusLabel(entry.toStatus)}
                                </span>
                                {entry.createdAt && (
                                    <span className="text-xs text-slate-500">{formatTimestamp(entry.createdAt)}</span>
                                )}
                            </div>
                            {entry.message && (
                                <p className="mt-1 text-xs text-slate-600">{entry.message}</p>
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

export default SftpTransferTimeline;
